import {
  EntityLinks,
  Goal,
  GoalGroupBy,
  GoalProgressCount,
  GoalRoadmapSortingKeys,
  GoalRoadmapSortingOptions,
  GoalSortingMode,
  GoalTreeIndex,
  GroupByMode,
  ID,
  LifeArea,
} from '@griply/types';

import { buildParentToChildrenMap } from './goals/get-sub-goals';
import { hasGoalParent } from './goals/has-goal-parent';
import { sortGoals } from './sort-goals';

export type BuildSortedGoalTreeOptions<
  TGoal extends Goal = Goal,
  TLifeArea extends LifeArea = LifeArea,
> = {
  goals: TGoal[];
  sortedLifeAreas: TLifeArea[];
  goalGroupBy?: GoalGroupBy;
  goalSortingMode?: GoalSortingMode;
  goalRoadmapSorting?: Partial<Record<GoalRoadmapSortingOptions, ID[]>>;
  goalGroupSorting?: {
    lifeArea?: {
      sorting: Record<ID, ID[]>;
    };
  };
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  index?: GoalTreeIndex<TGoal>;
  links: EntityLinks;
};

export const buildSortedGoalTree = <
  TGoal extends Goal,
  TLifeArea extends LifeArea,
>({
  goals,
  sortedLifeAreas,
  goalGroupBy,
  goalSortingMode,
  goalRoadmapSorting,
  goalGroupSorting,
  taskCounts,
  goalProgressCounts,
  index,
  links,
}: BuildSortedGoalTreeOptions<TGoal, TLifeArea>): TGoal[] => {
  if (!goals.length) {
    return [];
  }

  const rootGoals = goals.filter((goal) => !hasGoalParent(goal, { links }));

  const customOrder =
    goalRoadmapSorting?.[GoalRoadmapSortingKeys.InProgress] ?? [];

  const validLifeAreaIds = new Set(sortedLifeAreas.map((la) => la.id));
  const lifeAreaOrder = new Map(
    sortedLifeAreas.map((la, index) => [la.id, index]),
  );

  const sortMode = goalSortingMode ?? GoalSortingMode.StartDate;

  const sortedRootGoals =
    goalGroupBy === GroupByMode.LifeArea
      ? [
          ...sortedLifeAreas
            .map((lifeArea) =>
              sortGoals(
                rootGoals.filter(
                  ({ lifeAreaId }) => lifeAreaId === lifeArea.id,
                ),
                {
                  sortMode,
                  allGoals: goals,
                  customOrder: lifeArea.goalSorting ?? [],
                  taskCounts,
                  goalProgressCounts,
                  links,
                },
              ),
            )
            .flat(),
          ...sortGoals(
            rootGoals.filter(
              ({ lifeAreaId }) =>
                !lifeAreaId || !validLifeAreaIds.has(lifeAreaId),
            ),
            {
              allGoals: goals,
              sortMode,
              customOrder: goalGroupSorting?.lifeArea?.sorting?.['null'] ?? [],
              taskCounts,
              goalProgressCounts,
              links,
            },
          ),
        ]
      : sortGoals(rootGoals, {
          allGoals: goals,
          sortMode,
          customOrder,
          lifeAreaOrder,
          taskCounts,
          goalProgressCounts,
          links,
        });

  // Built once — the previous per-node `goals.filter` made this O(n^2).
  const childrenByParent =
    index?.childrenByParentId ?? buildParentToChildrenMap(goals, links);

  // `visited` guards against a corrupt parent cycle sending the walk infinite.
  const visited = new Set<ID>();

  const processGoals = (parentGoals: TGoal[]): TGoal[] =>
    parentGoals.flatMap((parentGoal) => {
      if (visited.has(parentGoal.id)) {
        return [];
      }
      visited.add(parentGoal.id);

      // The index's arrays outlive this call, so hand sortGoals a copy.
      const childGoals = [...(childrenByParent.get(parentGoal.id) ?? [])];

      const sortedChildGoals = sortGoals(childGoals, {
        sortMode,
        customOrder: parentGoal.goalSorting ?? [],
        allGoals: goals,
        taskCounts,
        goalProgressCounts,
        links,
      });

      return [parentGoal, ...processGoals(sortedChildGoals)];
    });

  return processGoals(sortedRootGoals);
};
